import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { aggregateFeeDues } from "../../feeDue/hooks/useStudentFeeDues";
import { getFeeDuesByStudent } from "../../feeDue/services/feeDuesService";
import { FeeDue } from "../../feeDue/types";
import { getLoggedInUserAdmissionNo, getLoggedInUserName } from "../../../modules/common/user/UserService";
import { formatLastUpdated, useCachedDashboard } from "../hooks/useCachedDashboard";

import { StudentDashboardHeader } from "./components/StudentDashboardHeader";
import { StatsRow } from "./components/StatsRow";
import { FeeDuesCard } from "./components/FeeDuesCard";
import { AttendanceDonutCard } from "./components/AttendanceDonutCard";
import { AttendanceTrendCard } from "./components/AttendanceTrendCard";
import { SubjectGradesCard } from "./components/SubjectGradesCard";
import { TodayTimetableCard } from "./components/TodayTimetableCard";
import { UpcomingEventsCard } from "./components/UpcomingEventsCard";

import {
  attendanceMonthly,
  attendanceYearly,
  subjectGrades,
  classAverageScore,
  todayTimetable,
  upcomingItems,
} from "./mockData";

// Derives "2025-26" from an academic year string like "2026-27". Falls back
// to a generic label if the shape doesn't match — the API only gives us the
// current academic year, not the previous one.
const previousAcademicYearLabel = (academicYear: string | undefined): string => {
  const match = academicYear?.match(/^(\d{4})-(\d{2})$/);
  if (!match) return "Previous Year";
  const startYear = parseInt(match[1], 10) - 1;
  return `${startYear}-${String(startYear + 1).slice(2)}`;
};

// Student dashboard. Fee Dues is real data (cached, refetched
// only after login or on refresh); Attendance, Grades, Timetable and
// Events don't have a student-scoped read API yet, so those widgets render
// empty states (badged "Coming soon") until their hooks exist.
export const StudentDashboard = () => {
  const navigate = useNavigate();
  const { data, fetchedAt, loading, refreshing, error, refresh } = useCachedDashboard("student", async () => {
    const admissionNo = getLoggedInUserAdmissionNo();
    const aggregated = aggregateFeeDues(await getFeeDuesByStudent(admissionNo), admissionNo);
    // wrapped so a student with no dues still caches a (non-null) result
    return { aggregatedFeeDue: aggregated ?? null } as { aggregatedFeeDue: FeeDue | null };
  });
  const aggregatedFeeDue = data?.aggregatedFeeDue ?? undefined;

  const studentName = getLoggedInUserName();

  const amountDue = aggregatedFeeDue?.payable ?? 0;
  const previousYearTotal = aggregatedFeeDue?.previousTotalDue ?? 0;
  const academicYear = aggregatedFeeDue?.academicYear ?? "";
  const feeStatus: "paid" | "pending" = amountDue <= 0 ? "paid" : "pending";

  const attendanceTotal = attendanceMonthly.present + attendanceMonthly.absent + attendanceMonthly.leave;
  const attendancePercent = attendanceTotal === 0 ? null : Math.round((attendanceMonthly.present / attendanceTotal) * 1000) / 10;

  const averageGrade =
    subjectGrades.length === 0 ? null : Math.round(subjectGrades.reduce((sum, s) => sum + s.score, 0) / subjectGrades.length);

  const nextEvent = upcomingItems.find((item) => item.kind === "event");

  const handlePayNow = () => {
    navigate("/app/fees/pay", { state: { feeDue: aggregatedFeeDue } });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2.5, md: 3 }, maxWidth: 1600, mx: "auto", width: "100%", px: { xs: 0.5, md: 1 }, pb: 3 }}>
      <StudentDashboardHeader
        studentName={studentName}
        lastUpdatedLabel={formatLastUpdated(fetchedAt)}
        refreshing={refreshing}
        onRefresh={refresh}
      />

      <StatsRow
        attendancePercentLabel={attendancePercent === null ? "—" : `${attendancePercent}%`}
        attendanceMeta={attendancePercent === null ? "No data yet" : "This month"}
        gradePercentLabel={averageGrade === null ? "—" : `${averageGrade}%`}
        gradeMeta={averageGrade === null ? "No data yet" : "Term 1 average"}
        feesDueLabel={loading ? "—" : `₹${amountDue.toLocaleString("en-IN")}`}
        feesDueMeta={feeStatus === "paid" ? "All paid up" : "Payment pending"}
        nextEventTitle={nextEvent?.title ?? "—"}
        nextEventMeta={nextEvent ? `${nextEvent.month} ${nextEvent.day}` : ""}
      />

      <Box sx={{ display: "flex", gap: { xs: 2, md: 3 }, alignItems: "flex-start", flexDirection: { xs: "column", lg: "row" } }}>
        <Box sx={{ flex: 1, minWidth: 0, width: "100%", display: "flex", flexDirection: "column", gap: { xs: 2, md: 3 } }}>
          <FeeDuesCard
            isLoading={loading}
            error={error}
            amountDue={amountDue}
            status={feeStatus}
            academicYear={academicYear}
            currentYearTotal={amountDue}
            previousYearTotal={previousYearTotal}
            previousYearLabel={previousAcademicYearLabel(academicYear)}
            onPayNow={handlePayNow}
          />

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: { xs: 2, md: 3 } }}>
            <AttendanceDonutCard data={attendanceMonthly} />
            <AttendanceTrendCard data={attendanceYearly} />
          </Box>

          <SubjectGradesCard data={subjectGrades} classAverage={classAverageScore} />
        </Box>

        <Box sx={{ width: { xs: "100%", lg: 340 }, flexShrink: 0, display: "flex", flexDirection: "column", gap: { xs: 2, md: 3 } }}>
          <TodayTimetableCard periods={todayTimetable} />
          <UpcomingEventsCard items={upcomingItems} />
        </Box>
      </Box>
    </Box>
  );
};
